import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Briefcase, MapPin, Clock, Search, ArrowLeft, CheckCircle2,
  Upload, Building2, AlertTriangle, CalendarDays, CalendarX2, Home,
  FileText, X,
} from 'lucide-react';
import { plainTextToHtml } from '../../../shared/utils/richText';
import {
  emailError, experienceError, nameError, phoneError,
  sanitizeExperience, sanitizePhone,
} from '../../../shared/utils/contactValidation';

import { brandButtonStyle, brandGradient, readableTextOn } from '../../../shared/utils/brandColor';
import { BrandWordmark } from '../../../shared/components/BrandLogo';
import { ctcPlaceholder, formatBudget } from '../../../shared/utils/budget';

const API = process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api';

/**
 * The company's job board, rendered inside an iframe on their own website.
 *
 * Deliberately self-contained: no sidebar, no login, no app chrome, and its own
 * styles rather than the product's tokens — the host page's CSS must never leak
 * in, and this must never look like a different site bolted onto theirs.
 *
 * All three states — list, apply, confirmation — live in this one component, so
 * the candidate never leaves the iframe and the browser URL stays on the
 * company's careers page throughout.
 */

/**
 * How tall the apply dialog is allowed to get before the form scrolls inside it,
 * and the breathing room left around it. A cap rather than a fit: some backdrop
 * has to stay visible, both so the candidate can see they are still on the
 * careers page they came from and so there is somewhere to click to dismiss.
 */
const MODAL_MAX_H = 760;
const MODAL_GUTTER = 64;

const C = {
  ink: '#111827', body: '#4b5563', faint: '#6b7280', line: '#e5e7eb',
  soft: '#f9fafb', accent: '#640d5f', accent2: '#d91656', green: '#0e7a52', red: '#b42318',
};

// The colour this board is drawn in. The company picks it in Settings; until
// their jobs load — and for a company that never picked one — it is ours.
let BRAND = C.accent;
// The label colour, when the company fixed one rather than letting it follow
// the background.
let BRAND_TEXT = null;

/** The style for a primary button, in whatever colour this company uses. */
const primary = () => ({ ...brandButtonStyle(BRAND), ...(BRAND_TEXT ? { color: BRAND_TEXT } : {}) });

const S = {
  // colorScheme pins the board light: it renders in an iframe on the customer's
  // own careers page, and Chrome's auto-dark-mode otherwise repaints the form
  // controls dark while the page around them stays white.
  page: { fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif', color: C.body, background: '#fff', colorScheme: 'light', padding: '24px 20px 40px', minHeight: '100%' },
  wrap: { maxWidth: 880, margin: '0 auto' },
  h1: { fontSize: 22, fontWeight: 700, color: C.ink, margin: 0 },
  sub: { fontSize: 14, color: C.faint, marginTop: 4 },
  bar: { display: 'flex', gap: 10, flexWrap: 'wrap', margin: '20px 0 18px' },
  input: { flex: '1 1 200px', minWidth: 0, padding: '10px 12px', fontSize: 14, border: `1px solid ${C.line}`, borderRadius: 8, fontFamily: 'inherit', color: C.ink },
  select: { padding: '10px 12px', fontSize: 14, border: `1px solid ${C.line}`, borderRadius: 8, background: '#fff', fontFamily: 'inherit', color: C.ink },
  card: { border: `1px solid ${C.line}`, borderRadius: 10, padding: '18px 20px', marginBottom: 12, background: '#fff', transition: 'box-shadow .15s, border-color .15s' },
  title: { fontSize: 16.5, fontWeight: 650, color: C.ink, margin: 0 },
  meta: { display: 'flex', rowGap: 4, columnGap: 14, flexWrap: 'wrap', fontSize: 13, color: C.faint, marginTop: 7 },
  metaItem: { display: 'inline-flex', alignItems: 'center', gap: 5 },
  chip: { fontSize: 11.5, padding: '3px 9px', borderRadius: 999, background: C.soft, color: C.body, border: `1px solid ${C.line}` },
  btn: { padding: '9px 18px', fontSize: 14, fontWeight: 600, borderRadius: 8, cursor: 'pointer', border: 'none', fontFamily: 'inherit' },
  
  ghost: { background: '#fff', color: C.ink, border: `1px solid ${C.line}` },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 5 },
  field: { marginBottom: 14 },
  empty: { textAlign: 'center', padding: '48px 20px', color: C.faint },
  panel: { border: `1px solid ${C.line}`, borderRadius: 12, padding: '22px 24px', background: '#fff' },
  facts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, padding: '16px 0', borderTop: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}`, margin: '18px 0 20px' },
  factLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: C.faint },
  factValue: { fontSize: 14, color: C.ink, marginTop: 3, fontWeight: 550 },
  sectionHead: { fontSize: 13, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: C.faint, margin: '0 0 10px' },
  jd: { fontSize: 14.5, lineHeight: 1.75, color: C.body },
  alert: (ok) => ({ padding: '11px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? 'rgba(14,122,82,.08)' : 'rgba(180,35,24,.07)', border: `1px solid ${ok ? 'rgba(14,122,82,.3)' : 'rgba(180,35,24,.28)'}`, color: ok ? C.green : C.red }),

  // `fixed`, so the dim layer always covers the whole iframe with no gap at the
  // bottom, whatever height the board's own content happens to be. The explicit
  // `visible` is what lets the board hide itself behind the dialog without
  // hiding the dialog with it.
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20, background: 'rgba(17,24,39,.5)', visibility: 'visible', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '0 16px' },
  modal: { width: '100%', maxWidth: 620, background: '#fff', borderRadius: 14, boxShadow: '0 18px 48px rgba(17,24,39,.26)', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  modalHead: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '15px 18px', borderBottom: `1px solid ${C.line}`, flexShrink: 0 },
  modalBody: { flex: '1 1 auto', minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '18px 18px 22px' },
  closeX: { flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, padding: 0, borderRadius: 8, border: `1px solid ${C.line}`, background: '#fff', color: C.faint, cursor: 'pointer' },
};

/** Only shown when the company published a range; a half-open one misleads. */
function salaryLabel(j) {
  const lo = Number(j.budget_min), hi = Number(j.budget_max);
  if (!lo || !hi) return null;
  // A reversed range is a data-entry slip, not a salary. Showing "79 - 67"
  // reads as broken, so present the pair in the order that makes sense.
  return formatBudget({ ...j, budget_min: Math.min(lo, hi), budget_max: Math.max(lo, hi) }) || null;
}

/** One place that decides how an invalid field looks. */
function FieldError({ message }) {
  if (!message) return null;
  return (
    <div style={{ fontSize: 12.5, color: C.red, marginTop: 4 }}>{message}</div>
  );
}

const locationOf = (j) => [j.city, j.country].filter(Boolean).join(', ') || j.work_location || 'Remote';

/**
 * The server sends UTC with no marker on it ("2026-09-29T11:06:58"), and a
 * bare string like that is read as local time — five and a half hours out in
 * India. The Z puts it back.
 */
function asDate(iso) {
  if (!iso) return null;
  const marked = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(marked ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Whole days between two dates on the calendar the reader is looking at.
 *
 * Elapsed hours are the wrong measure for "today": something posted at 8pm is
 * still "today" at nine the next morning if you divide by 24, because only
 * thirteen hours have passed. Both ends drop to midnight first, so the label
 * turns over when the date does.
 */
function calendarDaysFrom(date) {
  const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((midnight(new Date()) - midnight(date)) / 86400000);
}

const fmtDate = (iso) => {
  const d = asDate(iso);
  return d ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
};

/** "today" / "3 days ago" reads better than a date for something just posted. */
function postedLabel(iso) {
  const d = asDate(iso);
  if (!d) return null;
  const days = calendarDaysFrom(d);
  if (days <= 0) return 'Posted today';
  if (days === 1) return 'Posted yesterday';
  if (days < 30) return `Posted ${days} days ago`;
  return `Posted ${fmtDate(iso)}`;
}

/** Urgency is the useful part of a deadline, so lead with the days left. */
function closingLabel(iso) {
  const d = asDate(iso);
  if (!d) return null;
  const days = -calendarDaysFrom(d);
  if (days < 0) return { text: `Closed ${fmtDate(iso)}`, urgent: true };
  if (days === 0) return { text: 'Closes today', urgent: true };
  if (days === 1) return { text: 'Closes tomorrow', urgent: true };
  if (days <= 7) return { text: `Closes in ${days} days`, urgent: true };
  return { text: `Closes ${fmtDate(iso)}`, urgent: false };
}

export default function EmbedJobBoard() {
  const { token } = useParams();
  // ?job=<code> opens that role straight away, which is what a shared link
  // carries. Browsing the board keeps the address in step, so the address bar
  // can be copied at any point.
  const [params, setParams] = useSearchParams();
  const openCode = params.get('job') || '';

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const [search, setSearch]   = useState('');
  const [dept, setDept]       = useState('');
  const [type, setType]       = useState('');
  const [loc, setLoc]         = useState('');
  const [work, setWork]       = useState('');

  // null = no dialog, otherwise the job being applied to. The list or the role
  // the candidate was reading stays mounted behind the dialog.
  const [applyTo, setApplyTo] = useState(null);
  const [applyAt, setApplyAt] = useState(0);
  const [detailFor, setDetailForState] = useState(null);

  const setDetailFor = useCallback((job) => {
    setDetailForState(job);
    const code = job?.public_code || '';
    setParams(code ? { job: code } : {}, { replace: true });
    // Embedded, our own address bar is not the one anybody can see. embed.js
    // puts this in the careers page's URL, so a visitor can copy that.
    if (window.parent !== window) {
      window.parent?.postMessage({ type: 'recruitai:job', code }, '*');
    }
  }, [setParams]);
  const [done, setDone]       = useState(null);

  // Where the candidate clicked, in the iframe's own viewport coordinates —
  // which is the coordinate space the dialog's `fixed` overlay lives in.
  //
  // We cannot know which slice of the iframe is actually on the visitor's
  // screen: the host page sets the iframe to a fixed height and tells us
  // nothing, so `100vh` in here is that fixed box and centring in it can land
  // the dialog below the fold. The one thing we do know is that the button they
  // just clicked was on screen, so that is what we aim at.
  const openApply = useCallback((job, e) => {
    setApplyAt(e?.clientY ?? 0);
    setApplyTo(job);
  }, []);

  const closeApply = useCallback(() => { setDone(null); setApplyTo(null); }, []);

  // A link like /j/<code> lands here with ?job=<code>: show that role. Embedded,
  // the code is on the careers page instead and reaches us from embed.js, which
  // repeats it — hence taking it once and only before anything is open.
  const [hostCode, setHostCode] = useState('');
  const tookHostCode = useRef(false);

  useEffect(() => {
    if (!openCode || !data?.jobs?.length) return;
    const job = data.jobs.find((j) => j.public_code === openCode);
    if (job) setDetailForState(job);
  }, [openCode, data]);

  // The host's code opens a role once and is then spent. Holding on to it
  // would reopen the role the moment the visitor pressed All positions.
  useEffect(() => {
    if (!hostCode || !data?.jobs?.length) return;
    const job = data.jobs.find((j) => j.public_code === hostCode);
    setHostCode('');
    if (job) setDetailFor(job);
  }, [hostCode, data, setDetailFor]);

  useEffect(() => {
    fetch(`${API}/portal/embed/${token}/jobs`)
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 404 ? 'This job board is no longer available.' : 'Could not load jobs.');
        return r.json();
      })
      .then((payload) => {
        BRAND = payload?.company?.brand_color || C.accent;
        BRAND_TEXT = payload?.company?.brand_text_color || null;
        // The apply form is styled by classes in the stylesheet, so the colour
        // reaches them as variables rather than inline styles.
        const root = document.documentElement.style;
        root.setProperty('--embed-brand', BRAND);
        root.setProperty('--embed-brand-gradient', brandGradient(BRAND));
        root.setProperty('--embed-brand-text', BRAND_TEXT || readableTextOn(BRAND));
        setData(payload);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  // Which slice of our iframe is actually on the visitor's screen, in our own
  // viewport coordinates. An iframe cannot see out — `window.innerHeight` in
  // here is the height the host gave the iframe, not the browser window — so
  // the embed snippet's script measures it for us and posts it over.
  //
  // Purely a layout hint, and treated as nothing more: two numbers, range
  // checked, used only to place a dialog. Older snippets have no script and
  // never send it, hence the null default and the fallback in ApplyDialog.
  const [band, setBand] = useState(null);

  // Whether the host has taken our frame fullscreen for the dialog. It tells us
  // the frame is no longer sitting in the page where the board belongs.
  const [full, setFull] = useState(false);

  useEffect(() => {
    const onMessage = (e) => {
      if (e.data?.type !== 'recruitai:viewport') return;
      if (!tookHostCode.current && typeof e.data.job === 'string' && e.data.job) {
        tookHostCode.current = true;
        setHostCode(e.data.job);
      }
      const frameTop = Number(e.data.frameTop);
      const windowHeight = Number(e.data.windowHeight);
      if (!Number.isFinite(frameTop) || !(windowHeight > 0)) return;
      const top = Math.max(0, -frameTop);
      const bottom = Math.min(window.innerHeight, windowHeight - frameTop);
      setFull(e.data.full === true);
      // A sliver means the board is scrolling past; not worth repositioning to.
      const next = bottom - top >= 240 ? { top, bottom } : null;
      // These arrive a few times a second. Only a band that actually moved is
      // worth a render — an open dialog reads its placement from every one.
      setBand((prev) =>
        (prev && next && prev.top === next.top && prev.bottom === next.bottom) ? prev : next);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Tell the host page how tall we are, so its snippet can size the iframe to
  // the board: a fixed frame either leaves blank space under the last role or
  // puts a scrollbar over the board, and we are the only side that knows which.
  // The snippet ignores this while the apply dialog is open, when it wants the
  // frame filling the window rather than hugging the content.
  //
  // Measured from the content itself, not from the body: the page fills the
  // frame so its white background reaches the bottom, which means the body is
  // always exactly as tall as whatever height the host last gave us. Asking it
  // would only ever echo that number back and the frame could never shrink.
  useEffect(() => {
    const content = () => document.querySelector('[data-board-content]');
    const post = () => {
      const el = content();
      const pad = el ? parseFloat(getComputedStyle(el.parentNode).paddingBottom) || 0 : 0;
      const h = el ? el.offsetTop + el.offsetHeight + pad : document.body.scrollHeight;
      window.parent?.postMessage({ type: 'recruitai:height', height: h }, '*');
    };
    post();
    const el = content();
    if (!el) return undefined;
    const ro = new ResizeObserver(post);
    ro.observe(el);
    return () => ro.disconnect();
  }, [data, applyTo, detailFor, done]);

  if (loading) return <div style={S.page}><div style={S.empty}>Loading open positions…</div></div>;

  if (error) {
    return (
      <div style={S.page}>
        <div style={{ ...S.empty, color: C.red }}>
          <AlertTriangle size={26} strokeWidth={1.6} />
          <div style={{ marginTop: 10 }}>{error}</div>
        </div>
      </div>
    );
  }

  // While the frame is fullscreen it is covering the visitor's careers page, so
  // the board steps aside and lets that show through the dim instead — a modal
  // over the site they are actually on, rather than over a board that has jumped
  // to the top of their screen. `visibility` rather than unmounting: the board
  // keeps its layout and scroll position, so closing puts it back untouched, and
  // the overlay switches itself visible again.
  const behind = full && applyTo ? { visibility: 'hidden' } : null;
  const pageStyle = behind ? { ...S.page, ...behind, background: 'transparent' } : S.page;

  // The apply dialog rides on top of whichever view is showing, so it is
  // rendered by both of them from one place.
  const applyDialog = applyTo && (
    <ApplyDialog
      job={applyTo}
      company={data.company.name}
      done={done}
      clickedAt={applyAt}
      band={band}
      seeThrough={!!behind}
      onClose={closeApply}
      onDone={(name) => setDone({ name, job: applyTo.title })}
    />
  );

  // ── Job details ──────────────────────────────────────────────────────────
  // The list only had Apply, so a candidate had to commit to a role before
  // reading what it was. Facts first, then the description, with Apply reachable
  // from the header without scrolling.
  if (detailFor) {
    const j = detailFor;
    const closing = closingLabel(j.closing_date);
    const facts = [
      ['Location', locationOf(j)],
      ['Employment', j.employment_type],
      ['Experience', j.min_experience_years ? `${j.min_experience_years}+ years` : null],
      ['Work mode', j.work_location],
      ['Department', j.department],
      ['Salary', salaryLabel(j)],
    ].filter(([, v]) => v);

    return (
      <div style={pageStyle}>
        <div style={S.wrap} data-board-content>
          <button style={{ ...S.btn, ...S.ghost, marginBottom: 18 }} onClick={() => setDetailFor(null)}>
            <ArrowLeft size={14} style={{ verticalAlign: '-2px' }} /> All positions
          </button>

          <div style={S.panel}>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <h1 style={S.h1}>{j.title}</h1>
                {j.job_code && (
                  <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700, color: C.ink }}>
                    Job Code: <span style={{ fontFamily: 'ui-monospace, monospace' }}>{j.job_code}</span>
                  </div>
                )}
                <div style={{ ...S.meta, marginTop: 8 }}>
                  {postedLabel(j.created_at) && (
                    <span style={S.metaItem}><CalendarDays size={13} />{postedLabel(j.created_at)}</span>
                  )}
                  {closing && (
                    <span style={{ ...S.metaItem, color: closing.urgent ? C.red : C.faint, fontWeight: closing.urgent ? 600 : 400 }}>
                      <CalendarX2 size={13} />{closing.text}
                    </span>
                  )}
                </div>
              </div>
              <button
                style={{ ...S.btn, ...primary(), flexShrink: 0 }}
                onClick={(e) => openApply(j, e)}
              >
                Apply for this role
              </button>
            </div>

            <div style={S.facts}>
              {facts.map(([label, value]) => (
                <div key={label}>
                  <div style={S.factLabel}>{label}</div>
                  <div style={S.factValue}>{value}</div>
                </div>
              ))}
            </div>

            {(j.required_skills || []).length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <div style={S.sectionHead}>Skills</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {j.required_skills.map((sk) => <span key={sk} style={S.chip}>{sk}</span>)}
                  {(j.preferred_skills || []).map((sk) => (
                    <span key={sk} style={{ ...S.chip, background: '#fff', color: C.faint }}>
                      {sk} · nice to have
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div style={S.sectionHead}>About this role</div>
            {j.job_description ? (
              // Sanitised server-side on save, so only allowlisted formatting
              // tags can reach this page.
              <div className="embed-jd" style={S.jd} dangerouslySetInnerHTML={{ __html: plainTextToHtml(j.job_description) }} />
            ) : (
              <div style={{
                display: 'flex', gap: 9, alignItems: 'flex-start',
                background: C.soft, border: `1px solid ${C.line}`, borderRadius: 8,
                padding: '12px 14px', fontSize: 14, color: C.faint,
              }}>
                <FileText size={16} strokeWidth={1.7} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>
                  No description was added for this role. The facts above are everything
                  the team has published — apply and they can tell you more.
                </span>
              </div>
            )}
          </div>

          <div style={{
            display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
            marginTop: 18,
          }}>
            <button
              style={{ ...S.btn, ...primary() }}
              onClick={(e) => openApply(j, e)}
            >
              Apply for this role
            </button>
            <span style={{ fontSize: 13, color: C.faint }}>
              Takes a minute — name, contact details and your CV.
            </span>
          </div>
        </div>
        {applyDialog}
      </div>
    );
  }

  // ── Job list ─────────────────────────────────────────────────────────────
  const f = data.filters;
  const jobs = data.jobs.filter((j) => {
    const q = search.trim().toLowerCase();
    const matchQ = !q
      || j.title?.toLowerCase().includes(q)
      || j.department?.toLowerCase().includes(q)
      || (j.required_skills || []).some((s) => s.toLowerCase().includes(q));
    return matchQ
      && (!dept || j.department === dept)
      && (!type || j.employment_type === type)
      && (!loc || locationOf(j) === loc)
      && (!work || j.work_location === work);
  });

  return (
    <div style={pageStyle}>
      <div style={S.wrap} data-board-content>
        <h1 style={S.h1}>Open positions at {data.company.name}</h1>
        <p style={S.sub}>
          {data.total} {data.total === 1 ? 'role' : 'roles'} currently open
        </p>

        <div style={S.bar}>
          <span style={{ position: 'relative', flex: '1 1 220px', display: 'flex' }}>
            <Search size={15} color={C.faint} style={{ position: 'absolute', left: 11, top: 12 }} />
            <input
              style={{ ...S.input, paddingLeft: 33 }}
              placeholder="Search roles or skills…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </span>
          {f.departments.length > 0 && (
            <select style={S.select} value={dept} onChange={(e) => setDept(e.target.value)}>
              <option value="">All departments</option>
              {f.departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          {f.locations.length > 0 && (
            <select style={S.select} value={loc} onChange={(e) => setLoc(e.target.value)}>
              <option value="">All locations</option>
              {f.locations.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          )}
          {f.employment_types.length > 0 && (
            <select style={S.select} value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">All types</option>
              {f.employment_types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          {f.work_locations?.length > 0 && (
            <select style={S.select} value={work} onChange={(e) => setWork(e.target.value)}>
              <option value="">Remote &amp; on-site</option>
              {f.work_locations.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
          )}
        </div>

        {jobs.length === 0 ? (
          <div style={S.empty}>
            <Briefcase size={26} strokeWidth={1.5} />
            <div style={{ marginTop: 10 }}>
              {data.total === 0
                ? 'There are no open positions right now. Please check back soon.'
                : 'No roles match those filters.'}
            </div>
          </div>
        ) : jobs.map((j) => (
          <div
            key={j.id}
            style={S.card}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(17,24,39,.07)'; e.currentTarget.style.borderColor = '#d1d5db'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = C.line; }}
          >
            <div className="job-card-header">
              <div style={{ minWidth: 0 }}>
                <h2 style={S.title}>{j.title}</h2>
                {j.job_code && (
                  <div style={{ marginTop: 4, fontSize: 12.5, fontWeight: 700, color: C.ink }}>
                    Job Code: <span style={{ fontFamily: 'ui-monospace, monospace' }}>{j.job_code}</span>
                  </div>
                )}
                <div style={S.meta}>
                  {j.department && <span style={S.metaItem}><Building2 size={13} />{j.department}</span>}
                  <span style={S.metaItem}><MapPin size={13} />{locationOf(j)}</span>
                  {j.employment_type && <span style={S.metaItem}><Clock size={13} />{j.employment_type}</span>}
                  {j.min_experience_years ? <span style={S.metaItem}><Briefcase size={13} />{j.min_experience_years}+ yrs</span> : null}
                  {j.work_location && <span style={S.metaItem}><Home size={13} />{j.work_location}</span>}
                </div>
                <div style={{ ...S.meta, marginTop: 6 }}>
                  {postedLabel(j.created_at) && (
                    <span style={S.metaItem}><CalendarDays size={13} />{postedLabel(j.created_at)}</span>
                  )}
                  {closingLabel(j.closing_date) && (
                    <span style={{
                      ...S.metaItem,
                      color: closingLabel(j.closing_date).urgent ? C.red : C.faint,
                      fontWeight: closingLabel(j.closing_date).urgent ? 600 : 400,
                    }}>
                      <CalendarX2 size={13} />{closingLabel(j.closing_date).text}
                    </span>
                  )}
                </div>
                {(j.required_skills || []).length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                    {j.required_skills.slice(0, 6).map((s) => <span key={s} style={S.chip}>{s}</span>)}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
                <button style={{ ...S.btn, ...S.ghost }} onClick={() => setDetailFor(j)}>
                  View details
                </button>
                <button style={{ ...S.btn, ...primary() }} onClick={(e) => openApply(j, e)}>
                  Apply
                </button>
              </div>
            </div>
          </div>
        ))}

        <div style={{ textAlign: 'center', fontSize: 11.5, color: '#9ca3af', marginTop: 26 }}>
          Powered by <BrandWordmark size={12.5} weight={700} color="inherit" />
        </div>
      </div>
      {applyDialog}
    </div>
  );
}

// ── Apply dialog ───────────────────────────────────────────────────────────
/**
 * The apply flow as a dialog rather than its own view: the candidate keeps the
 * list — or the role they were reading — behind them, and closing puts them
 * back exactly where they were instead of at the top of the board.
 *
 * The confirmation lives here too, so a submitted application does not throw
 * away the view underneath it.
 */
function ApplyDialog({ job, company, done, clickedAt, band, seeThrough, onClose, onDone }) {
  const [busy, setBusy] = useState(false);

  // Our own viewport height, read on every render rather than held in state: the
  // host grows the frame the instant we open, and being resized from outside
  // fires neither a `resize` event nor a ResizeObserver in here, so anything
  // cached would stay set to the smaller frame we opened in. What does arrive is
  // the host's next viewport message, and re-rendering on that reads this fresh.
  const vh = window.innerHeight;

  // With a band from the host we know exactly what the visitor can see, so we
  // centre in it — and once the frame is fullscreen the band is the whole frame,
  // so this is a plain centre on screen. Without one — an older embed snippet —
  // the click is the only point we know is on screen, and a point on screen is
  // on average mid-screen, so straddling it is the best guess going.
  const view = band || { top: 0, bottom: vh };
  const height = Math.min(MODAL_MAX_H, view.bottom - view.top - MODAL_GUTTER);
  const centreOf = (a, b) => a + (b - a - height) / 2;
  const ideal = band ? centreOf(view.top, view.bottom) : clickedAt - height / 2;

  // If the ideal position does not fit the iframe, centre in it rather than
  // clamping: clamping shoves the dialog flat against one edge and piles all
  // the slack against the other, which reads as a layout bug.
  const lo = MODAL_GUTTER / 2;
  const hi = vh - height - lo;
  const top = ideal >= lo && ideal <= hi ? ideal : Math.max(lo, centreOf(0, vh));

  // Ask the host to hand us the whole window while we are up. Two things need
  // it: the frame is otherwise only as tall as the board, which would trap the
  // dialog inside it, and the careers page behind can still be scrolled — we
  // cannot reach its scrollbar from in here.
  useEffect(() => {
    const tell = (open) => window.parent?.postMessage({ type: 'recruitai:dialog', open }, '*');
    tell(true);
    return () => tell(false);
  }, []);

  // Closing mid-submit would lose an application that is already on its way to
  // the server, so the escape hatches are shut while it is in flight.
  const tryClose = useCallback(() => { if (!busy) onClose(); }, [busy, onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') tryClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tryClose]);

  // The dialog does not move with the board, so let nothing move behind it.
  // Both elements: the scrolling one is the html element, and hiding overflow on
  // the body alone leaves the page scrollable.
  useEffect(() => {
    const root = document.documentElement;
    const prev = [root.style.overflow, document.body.style.overflow];
    root.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      root.style.overflow = prev[0];
      document.body.style.overflow = prev[1];
    };
  }, []);

  // The board hides itself while the frame is fullscreen; the document's own
  // background would still paint white over the careers page underneath, so it
  // has to come off too. The app stylesheet sets it, hence the explicit reset.
  useEffect(() => {
    if (!seeThrough) return undefined;
    const root = document.documentElement;
    const prev = [root.style.background, document.body.style.background];
    root.style.background = 'transparent';
    document.body.style.background = 'transparent';
    return () => {
      root.style.background = prev[0];
      document.body.style.background = prev[1];
    };
  }, [seeThrough]);

  return (
    <div
      style={S.overlay}
      // Only a click on the dim area itself closes — one that started inside
      // the form and drifted out (selecting text, dragging a file) must not.
      onMouseDown={(e) => { if (e.target === e.currentTarget) tryClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={done ? 'Application received' : `Apply for ${job.title}`}
    >
      <div style={{ ...S.modal, marginTop: top, maxHeight: height }}>
        <div style={S.modalHead}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ ...S.title, fontSize: 16 }}>
              {done ? 'Application received' : job.title}
            </div>
            {!done && (
              <div style={{ ...S.meta, marginTop: 5 }}>
                <span style={S.metaItem}><MapPin size={13} />{locationOf(job)}</span>
                {job.employment_type && (
                  <span style={S.metaItem}><Clock size={13} />{job.employment_type}</span>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={tryClose}
            disabled={busy}
            aria-label="Close"
            style={{ ...S.closeX, opacity: busy ? 0.5 : 1, cursor: busy ? 'default' : 'pointer' }}
          >
            <X size={17} />
          </button>
        </div>

        <div style={S.modalBody}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
              <CheckCircle2 size={40} strokeWidth={1.6} color={C.green} />
              <p style={{ ...S.sub, maxWidth: '46ch', margin: '12px auto 0' }}>
                Thanks {done.name} — your application for <strong>{done.job}</strong> is
                with the {company} hiring team. They will be in touch by email.
              </p>
              <button style={{ ...S.btn, ...S.ghost, marginTop: 20 }} onClick={onClose}>
                <ArrowLeft size={14} style={{ verticalAlign: '-2px' }} /> Back to open positions
              </button>
            </div>
          ) : (
            <ApplyForm job={job} onBusy={setBusy} onDone={onDone} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Apply form ─────────────────────────────────────────────────────────────
function ApplyForm({ job, onDone, onBusy }) {
  const [f, setF] = useState({
    name: '', email: '', phone: '', current_role: '', experience_years: '',
    location: '', work_mode: '', current_ctc: '', expected_ctc: '', notice_period: '', cover_note: '',
  });
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [touched, setTouched] = useState({});

  // The dialog around us locks its close controls while a submit is in flight.
  useEffect(() => { onBusy?.(busy); }, [busy, onBusy]);

  // The form used to accept a name of pure digits, an email with no @ and a
  // phone made of letters. Checked here for immediate feedback; the server
  // checks the same things, because a form is only a suggestion.
  const fieldErrors = {
    name: nameError(f.name),
    email: emailError(f.email, { required: true }),
    phone: phoneError(f.phone, { required: true }),
    experience_years: experienceError(f.experience_years),
    location: f.location.trim() ? null : 'Preferred location is required',
    work_mode: f.work_mode ? null : 'Choose a work mode',
  };
  const blocking = Object.values(fieldErrors).some(Boolean);
  const showErr = (k) => (touched[k] ? fieldErrors[k] : null);
  const markTouched = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  // Email verification. `verifiedFor` records which address the token belongs
  // to, so editing the email after verifying correctly un-verifies it rather
  // than applying under an address nobody proved.
  const [otp, setOtp] = useState({
    sent: false, code: '', token: '', verifiedFor: '', busy: false, note: '', error: '',
  });
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verified = Boolean(otp.token) && otp.verifiedFor === f.email.trim().toLowerCase();

  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const sendCode = async () => {
    if (fieldErrors.email) { setTouched((t) => ({ ...t, email: true })); return; }
    setOtp((o) => ({ ...o, busy: true, error: '', note: '' }));
    try {
      const res = await fetch(`${API}/portal/apply/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: job.id, email: f.email.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Could not send the code.');
      setOtp((o) => ({ ...o, sent: true, busy: false, code: '', note: `Code sent to ${f.email.trim()}.` }));
      setCooldown(60);
    } catch (e2) {
      setOtp((o) => ({ ...o, busy: false, error: e2.message }));
    }
  };

  const verifyCode = async () => {
    setOtp((o) => ({ ...o, busy: true, error: '', note: '' }));
    try {
      const res = await fetch(`${API}/portal/apply/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: job.id, email: f.email.trim(), code: otp.code }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Could not verify that code.');
      setOtp((o) => ({
        ...o, busy: false, token: d.verification_token, verifiedFor: f.email.trim().toLowerCase(), note: '',
      }));
    } catch (e2) {
      setOtp((o) => ({ ...o, busy: false, error: e2.message }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (blocking) {
      setTouched({ name: true, email: true, phone: true, experience_years: true, location: true, work_mode: true });
      setErr('Please correct the highlighted fields.');
      return;
    }
    setErr('');
    if (!verified) { setErr('Please verify your email address first.'); return; }
    if (!resume) { setErr('Please attach your resume.'); return; }

    setBusy(true);
    // Multipart, because the resume is a file — this is the same endpoint the
    // hosted portal uses, so an embedded application reaches the pipeline by
    // exactly the same path.
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append('job_id', job.id);
    fd.append('resume', resume);
    fd.append('verification_token', otp.token);

    try {
      const res = await fetch(`${API}/portal/apply`, { method: 'POST', body: fd });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Could not submit your application.');
      onDone(f.name);
    } catch (e2) {
      setErr(e2.message);
      setBusy(false);
    }
  };

  return (
    <div>
      {/* No job header: the dialog's own head already names the role. The
          stylesheet's top margin assumed a header above it, so it goes. */}
      <form onSubmit={submit} className="application-form" style={{ marginTop: 0 }}>
        {err && (
          <div className="modern-alert error full-width">
            <AlertTriangle size={18} />
            <span>{err}</span>
          </div>
        )}
        {/* Full Name */}
        <div className="form-field">
          <label className="modern-label" htmlFor="ap-name">
            Full name <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            id="ap-name"
            className="modern-input"
            required
            value={f.name}
            onChange={set('name')}
            onBlur={markTouched('name')}
            placeholder="Jane Doe"
          />
          <FieldError message={showErr('name')} />
        </div>
        {/* Email */}
        <div className="form-field">
          <label className="modern-label" htmlFor="ap-email">
            Email <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            id="ap-email"
            type="email"
            className="modern-input"
            required
            value={f.email}
            readOnly={verified}
            onChange={(e) => {
              setF((v) => ({ ...v, email: e.target.value }));
              // A token belongs to one address. Changing it starts over.
              setOtp((o) => ({ ...o, sent: false, code: '', token: '', verifiedFor: '', note: '', error: '' }));
            }}
            onBlur={markTouched('email')}
            placeholder="jane@example.com"
          />
          <FieldError message={showErr('email')} />
        </div>

        {/* We email a code before accepting the application, so the address on
            a candidate record is one somebody actually holds — and so "one
            application per job" means something. */}
        <div className="form-field full-width">
          <div style={{ ...S.panel, padding: 14 }}>
            {verified ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: C.green, fontWeight: 600 }}>
                <CheckCircle2 size={16} /> Email verified
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13.5, color: C.faint }}>
                    {otp.sent ? 'Enter the 6-digit code we emailed you.' : 'Verify your email to continue.'}
                  </span>
                  {!otp.sent && (
                    <button type="button" onClick={sendCode} disabled={otp.busy}
                      style={{ ...S.btn, ...primary(), padding: '8px 14px', fontSize: 13 }}>
                      {otp.busy ? 'Sending…' : 'Send code'}
                    </button>
                  )}
                </div>

                {otp.sent && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                    <input
                      inputMode="numeric" maxLength={6} placeholder="123456" value={otp.code}
                      onChange={(e) => setOtp((o) => ({ ...o, code: e.target.value.replace(/\D/g, '').slice(0, 6), error: '' }))}
                      className="modern-input"
                      style={{ width: 120, letterSpacing: 3, textAlign: 'center' }}
                    />
                    <button type="button" onClick={verifyCode} disabled={otp.busy || otp.code.length < 6}
                      style={{ ...S.btn, ...primary(), padding: '8px 14px', fontSize: 13, opacity: otp.code.length < 6 ? 0.6 : 1 }}>
                      {otp.busy ? 'Checking…' : 'Verify'}
                    </button>
                    <button type="button" onClick={sendCode} disabled={otp.busy || cooldown > 0}
                      style={{ ...S.btn, ...S.ghost, padding: '8px 14px', fontSize: 13, opacity: cooldown > 0 ? 0.6 : 1 }}>
                      {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                    </button>
                  </div>
                )}

                {otp.note && <div style={{ fontSize: 12.5, color: C.faint, marginTop: 8 }}>{otp.note}</div>}
                {otp.error && <FieldError message={otp.error} />}
              </>
            )}
          </div>
        </div>
        {/* Phone */}
        <div className="form-field">
          <label className="modern-label" htmlFor="ap-phone">
            Phone <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            id="ap-phone"
            className="modern-input"
            required
            inputMode="tel"
            value={f.phone}
            onChange={(e) => setF((v) => ({ ...v, phone: sanitizePhone(e.target.value) }))}
            onBlur={markTouched('phone')}
            placeholder="+91 98765 43210"
          />
          <FieldError message={showErr('phone')} />
        </div>
        {/* Current Role */}
        <div className="form-field">
          <label className="modern-label" htmlFor="ap-role">
            Current role <span className="optional">(optional)</span>
          </label>
          <input
            id="ap-role"
            className="modern-input"
            value={f.current_role}
            onChange={set('current_role')}
            placeholder="Backend Engineer"
          />
        </div>
        {/* Years of Experience */}
        <div className="form-field">
          <label className="modern-label" htmlFor="ap-exp">
            Years of experience <span className="optional">(optional)</span>
          </label>
          <input
            id="ap-exp"
            inputMode="decimal"
            className="modern-input"
            value={f.experience_years}
            onChange={(e) => setF((v) => ({ ...v, experience_years: sanitizeExperience(e.target.value) }))}
            onBlur={markTouched('experience_years')}
            placeholder="5"
            style={{ maxWidth: '100%' }}
          />
          <FieldError message={showErr('experience_years')} />
        </div>
        {/* Preferred Location */}
        <div className="form-field">
          <label className="modern-label" htmlFor="ap-location">
            Preferred location <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            id="ap-location"
            className="modern-input"
            required
            maxLength={200}
            value={f.location}
            onChange={set('location')}
            onBlur={markTouched('location')}
            placeholder="e.g. Bengaluru"
          />
          <FieldError message={showErr('location')} />
        </div>
        {/* Work Mode */}
        <div className="form-field">
          <label className="modern-label" htmlFor="ap-work-mode">
            Work mode <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <select
            id="ap-work-mode"
            className="modern-input"
            required
            value={f.work_mode}
            onChange={set('work_mode')}
            onBlur={markTouched('work_mode')}
          >
            <option value="">Select…</option>
            {['On-site', 'Remote', 'Hybrid'].map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <FieldError message={showErr('work_mode')} />
        </div>
        {/* Current Salary */}
        <div className="form-field">
          <label className="modern-label" htmlFor="ap-current-ctc">
            Current salary (CTC) <span className="optional">(optional)</span>
          </label>
          <input
            id="ap-current-ctc"
            className="modern-input"
            maxLength={100}
            value={f.current_ctc}
            onChange={set('current_ctc')}
            placeholder={ctcPlaceholder(job?.budget_currency, 'current')}
          />
        </div>
        {/* Expected Salary */}
        <div className="form-field">
          <label className="modern-label" htmlFor="ap-expected-ctc">
            Expected salary (CTC) <span className="optional">(optional)</span>
          </label>
          <input
            id="ap-expected-ctc"
            className="modern-input"
            maxLength={100}
            value={f.expected_ctc}
            onChange={set('expected_ctc')}
            placeholder={ctcPlaceholder(job?.budget_currency, 'expected')}
          />
        </div>
        {/* Notice Period */}
        <div className="form-field">
          <label className="modern-label" htmlFor="ap-notice">
            Notice period <span className="optional">(optional)</span>
          </label>
          <select
            id="ap-notice"
            className="modern-input"
            value={f.notice_period}
            onChange={set('notice_period')}
          >
            <option value="">Select…</option>
            {['Immediate', '15 days', '30 days', '45 days', '60 days', '90 days', 'More than 90 days'].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        {/* Resume Upload */}
        <div className="form-field">
          <label className="modern-label">
            Resume <span style={{ color: '#ef4444' }}>*</span>
            <span className="optional"> (PDF or DOCX, one file, max 3 pages)</span>
          </label>
          <div className="file-upload-wrapper">
            <label
              htmlFor="ap-cv"
              className={`file-upload-label ${resume ? 'has-file' : ''}`}
            >
              <Upload
                size={20}
                className="file-upload-icon"
                color={resume ? '#0e7a52' : '#6b7280'}
              />
              <span className="file-upload-text">
                {resume ? resume.name : 'Click to upload or drag and drop'}
              </span>
              {resume && (
                <CheckCircle2 size={20} color="#0e7a52" />
              )}
            </label>
            <input
              id="ap-cv"
              type="file"
              accept=".pdf,.doc,.docx"
              className="file-upload-input"
              onChange={(e) => setResume(e.target.files?.[0] || null)}
            />
          </div>
        </div>
        {/* Cover Note */}
        <div className="form-field full-width">
          <label className="modern-label" htmlFor="ap-note">
            Cover note <span className="optional">(optional)</span>
          </label>
          <textarea
            id="ap-note"
            className="modern-input modern-textarea"
            value={f.cover_note}
            onChange={set('cover_note')}
            placeholder="Why you are a good fit for this role, or anything the hiring team should know."
          />
        </div>
        {/* Submit Button */}
        <button
          type="submit"
          className="modern-submit full-width"
          disabled={busy}
          style={{ opacity: blocking ? 0.75 : 1 }}
          onClick={() => setTouched({ name: true, email: true, phone: true, experience_years: true, location: true, work_mode: true })}
        >
          {busy ? 'Submitting Application...' : 'Submit Application'}
        </button>
      </form>
    </div>
  );
}